/* VELSENOR v23 — one quiet, gesture-gated Web Audio system. No trackers or libraries. */
(() => {
  'use strict';
  const ready = callback => document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', callback, {once: true}) : callback();

  ready(() => {
    const root = document.querySelector('[data-voralis-sound]');
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    const button = root.querySelector('[data-voralis-sound-toggle]');
    const label = root.querySelector('[data-voralis-sound-label]');
    const status = root.querySelector('[data-voralis-sound-status]');
    if (!button) return;

    const preferenceKey = 'velsenor.sound.preference.v1';
    const positionKey = 'velsenor.sound.position.v1';
    const AudioEngine = window.AudioContext || window.webkitAudioContext;
    const musicEnabled = root.dataset.musicEnabled === 'true';
    const effectsEnabled = root.dataset.effectsEnabled === 'true';
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const designMode = Boolean(window.Shopify && window.Shopify.designMode);
    const clampVolume = (value, fallback, maximum) => {
      const number = value === '' || value == null ? fallback : Number(value);
      return Math.max(0, Math.min(maximum, Number.isFinite(number) ? number : fallback)) / 100;
    };
    const musicVolume = clampVolume(root.dataset.musicVolume, 6, 15);
    const effectsVolume = clampVolume(root.dataset.effectsVolume, 4, 12);
    const readPreference = () => {
      try {return localStorage.getItem(preferenceKey) !== 'muted';} catch (_) {return true;}
    };
    const storePreference = () => {
      try {localStorage.setItem(preferenceKey, enabled ? 'enabled' : 'muted');} catch (_) {}
    };
    let enabled = readPreference();
    let engaged = false;
    let failed = false;
    let context = null;
    let master = null;
    let musicGain = null;
    let effectsGain = null;
    let musicBuffer = null;
    let loadingBuffer = null;
    let musicSource = null;
    let musicStartedAt = 0;
    let musicStartOffset = 0;
    let revision = 0;
    let lastEffectAt = -Infinity;
    const voices = new Set();

    const canPlay = () => enabled && engaged && !failed && !document.hidden;
    const isPlaying = () => canPlay() && context && context.state === 'running';
    const updateControl = (announce = false) => {
      const playing = Boolean(isPlaying());
      const state = failed ? 'unavailable' : !enabled ? 'muted' : playing ? 'playing' : engaged ? 'paused' : 'ready';
      const text = failed && !AudioEngine ? root.dataset.labelUnavailable
        : playing ? root.dataset.labelDisable : root.dataset.labelEnable;
      root.dataset.state = state;
      root.dataset.audioContext = context ? context.state : 'uninitialized';
      root.dataset.musicState = musicSource ? 'loop-ready' : loadingBuffer ? 'loading' : 'idle';
      button.setAttribute('aria-pressed', String(playing));
      button.setAttribute('aria-label', text);
      button.title = text;
      if (label) label.textContent = text;
      if (announce && status) status.textContent = text;
    };

    // Background films never compete with the music. The switch also mutes any
    // other native media currently present when the visitor requests silence.
    const backgroundSelector = '.ambient-video,[data-lc-cinematic-video],[data-lc-hero-film],.video-story video';
    const silenceNativeMedia = (all = false) => {
      document.querySelectorAll(all ? 'video,audio' : backgroundSelector).forEach(media => {
        if (!media.muted) media.muted = true;
      });
    };
    silenceNativeMedia(!enabled);
    document.addEventListener('volumechange', event => {
      const media = event.target;
      if (media instanceof HTMLMediaElement && (!enabled || media.matches(backgroundSelector)) && !media.muted) {
        media.muted = true;
      }
    }, true);
    document.addEventListener('shopify:section:load', () => silenceNativeMedia(!enabled));

    const quiet = () => {
      if (context && master) {
        // Cancel scheduled fades before muting; late decode/resume promises are
        // separately guarded by revision so they cannot undo this decision.
        master.gain.cancelScheduledValues(context.currentTime);
        master.gain.setValueAtTime(0, context.currentTime);
        voices.forEach(voice => {try {voice.stop();} catch (_) {}});
        voices.clear();
        if (context.state === 'running') context.suspend().catch(() => {});
      }
      updateControl();
    };

    const createContext = () => {
      if (context) return context;
      if (!AudioEngine) throw new Error('Web Audio is unavailable');
      context = new AudioEngine();
      master = context.createGain();
      master.gain.value = 0;
      master.connect(context.destination);
      musicGain = context.createGain();
      musicGain.gain.value = 0;
      musicGain.connect(master);
      effectsGain = context.createGain();
      effectsGain.gain.value = effectsVolume;
      effectsGain.connect(master);
      context.addEventListener('statechange', () => updateControl());
      return context;
    };

    const loadMusic = () => {
      if (musicBuffer) return Promise.resolve(musicBuffer);
      if (loadingBuffer) return loadingBuffer;
      loadingBuffer = fetch(root.dataset.musicUrl, {credentials: 'omit', cache: 'force-cache'})
        .then(response => {
          if (!response.ok) throw new Error('Ambient track unavailable');
          return response.arrayBuffer();
        })
        .then(bytes => new Promise((resolve, reject) => {
          // Callback form also supports older WebKit AudioContext implementations.
          context.decodeAudioData(bytes, resolve, reject);
        }))
        .then(buffer => {
          musicBuffer = buffer;
          loadingBuffer = null;
          return buffer;
        }, error => {
          loadingBuffer = null;
          throw error;
        });
      updateControl();
      return loadingBuffer;
    };

    const startMusic = (buffer, token) => {
      if (!canPlay() || token !== revision || context.state !== 'running' || musicSource) return;
      let offset = 0;
      try {offset = Number(sessionStorage.getItem(positionKey) || 0);} catch (_) {}
      if (!Number.isFinite(offset) || offset < 0) offset = 0;
      musicStartOffset = offset % buffer.duration;
      musicSource = context.createBufferSource();
      musicSource.buffer = buffer;
      musicSource.loop = true;
      musicSource.connect(musicGain);
      musicStartedAt = context.currentTime;
      musicGain.gain.cancelScheduledValues(context.currentTime);
      musicGain.gain.setValueAtTime(0, context.currentTime);
      musicGain.gain.linearRampToValueAtTime(musicVolume, context.currentTime + 2.4);
      musicSource.start(0, musicStartOffset);
      updateControl();
    };

    const playAccent = () => {
      const now = performance.now();
      if (!effectsEnabled || effectsVolume === 0 || reducedMotion.matches || !isPlaying() || now - lastEffectAt < 2200) return;
      lastEffectAt = now;
      // A short, rounded two-tone acknowledgement — never a scroll/hover loop.
      [659.255, 987.767].forEach((frequency, index) => {
        const start = context.currentTime + index * 0.035;
        const oscillator = context.createOscillator();
        const envelope = context.createGain();
        oscillator.type = 'sine';
        oscillator.frequency.value = frequency;
        envelope.gain.setValueAtTime(0.0001, start);
        envelope.gain.exponentialRampToValueAtTime(index ? 0.22 : 0.4, start + 0.012);
        envelope.gain.exponentialRampToValueAtTime(0.0001, start + 0.2);
        oscillator.connect(envelope);
        envelope.connect(effectsGain);
        voices.add(oscillator);
        oscillator.onended = () => {voices.delete(oscillator); oscillator.disconnect(); envelope.disconnect();};
        oscillator.start(start);
        oscillator.stop(start + 0.23);
      });
    };

    const activate = (accent = false) => {
      if (!enabled || failed || document.hidden) return;
      engaged = true;
      const token = revision;
      let resumed;
      try {
        // This call happens synchronously inside the real click/key event.
        // A media fetch is intentionally never awaited before resume().
        const engine = createContext();
        // Always enqueue resume, even if a preceding rapid mute has a pending
        // suspend operation while state still reports running.
        resumed = engine.resume();
      } catch (_) {
        failed = true;
        button.disabled = !AudioEngine;
        updateControl();
        return;
      }
      updateControl();
      Promise.resolve(resumed).then(() => {
        if (!canPlay() || token !== revision || context.state !== 'running') {
          // A newer activation owns the context. Do not suspend its playback.
          if (!canPlay()) quiet();
          updateControl();
          return;
        }
        master.gain.cancelScheduledValues(context.currentTime);
        master.gain.setValueAtTime(master.gain.value, context.currentTime);
        master.gain.linearRampToValueAtTime(1, context.currentTime + 0.45);
        if (accent) playAccent();
        updateControl();
        if (musicEnabled && musicVolume > 0 && !musicSource) {
          loadMusic().then(buffer => startMusic(buffer, token)).catch(() => {
            if (token !== revision || !canPlay()) return;
            failed = true;
            quiet();
          });
        }
      }).catch(() => {
        // Browser denial is recoverable with the visible sound button.
        updateControl();
      });
    };

    const toggleSound = () => {
      if (isPlaying()) {
        enabled = false;
        revision += 1;
        storePreference();
        silenceNativeMedia(true);
        quiet();
      } else {
        enabled = true;
        failed = false;
        revision += 1;
        storePreference();
        activate();
      }
      updateControl(true);
    };
    button.addEventListener('click', toggleSound);

    const effectSelector = '[data-voralis-sfx],a.button,button.button,.header-contact,.cinematic-world__link,[data-hero-world-tab]';
    const textInput = 'input,textarea,select,[contenteditable="true"],[role="textbox"]';
    document.addEventListener('click', event => {
      if (!event.isTrusted || event.defaultPrevented || event.button > 0 || !enabled || failed || designMode) return;
      const target = event.target instanceof Element ? event.target : null;
      if (!target || target.closest('[data-voralis-sound]') || target.closest(textInput)) return;
      const action = target.closest(effectSelector);
      const accent = Boolean(action && !action.matches(':disabled,[aria-disabled="true"]'));
      if (isPlaying()) {if (accent) playAccent();} else activate(accent);
    }, {passive: true});
    document.addEventListener('keydown', event => {
      if (!event.isTrusted || event.repeat || event.defaultPrevented || !['Enter', ' '].includes(event.key) || !enabled || failed || designMode) return;
      const target = event.target instanceof Element ? event.target : null;
      if (target && target.closest(`${textInput},[data-voralis-sound]`)) return;
      // The following semantic click provides the accent, preventing duplicates.
      if (!isPlaying()) activate();
    }, {passive: true});

    document.addEventListener('visibilitychange', () => {
      revision += 1;
      if (document.hidden) quiet();
      else if (enabled && engaged && !failed) activate();
      else updateControl();
    });
    window.addEventListener('storage', event => {
      if (event.key !== preferenceKey) return;
      enabled = event.newValue !== 'muted';
      revision += 1;
      if (!enabled) {silenceNativeMedia(true); quiet();}
      else if (engaged && !document.hidden && !failed) activate();
      else updateControl();
    });
    window.addEventListener('pagehide', () => {
      if (context && musicBuffer && musicSource) {
        const position = (musicStartOffset + context.currentTime - musicStartedAt) % musicBuffer.duration;
        try {sessionStorage.setItem(positionKey, String(position));} catch (_) {}
      }
      revision += 1;
      quiet();
    });
    window.addEventListener('pageshow', event => {
      if (event.persisted && enabled && engaged && !failed) activate();
    });
    if (!AudioEngine) {failed = true; button.disabled = true;}
    root.hidden = false;
    updateControl();
  });
})();
